/**
 * Tabular export — CSV, Excel (.xlsx) and JSON, with no third-party dependency.
 *
 * The .xlsx writer builds a real Office Open XML workbook inside a ZIP archive
 * assembled by hand. Entries are stored uncompressed, which the ZIP spec allows
 * and every spreadsheet application accepts, so no deflate implementation is
 * needed.
 *
 * Two behaviours here are deliberate and load-bearing:
 *
 * 1. Every cell is written as text. Excel eagerly reinterprets bare values —
 *    it turns a phone number into a float and drops the leading zero, reads
 *    "EMP-001" inconsistently, and reformats anything date-shaped to the local
 *    locale. Employee data is full of identifiers that must survive a round
 *    trip intact, so nothing is emitted as a numeric or date cell.
 *
 * 2. Values are guarded against formula injection. A cell beginning with
 *    = + - @ (or tab/CR, which Excel strips before parsing) is treated as a
 *    formula when the file is opened, which turns an exported spreadsheet into
 *    an execution vector — the classic example being a DDE payload sitting in
 *    somebody's name field. Such values get an apostrophe prefix, which Excel
 *    reads as "this is literal text" and does not display.
 */

export interface ExportColumn<T> {
  /** Column heading in the output file. */
  header: string;
  /** Pulls the raw value for a row. Return null/undefined for a blank cell. */
  value: (row: T) => unknown;
}

export type ExportFormat = 'csv' | 'xlsx' | 'json';

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: 'CSV (.csv)',
  xlsx: 'Excel (.xlsx)',
  json: 'JSON (.json)',
};

/* -------------------------------------------------------------------------- */
/* value normalisation                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Render any value as a display string.
 *
 * Dates become ISO-8601 date-only, which sorts correctly as text and is
 * unambiguous across locales — unlike "03/04/2026", which is two different days
 * depending on who opens it.
 */
export function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (Array.isArray(value)) return value.map(stringifyValue).filter(Boolean).join('; ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
}

/**
 * Normalise a date-ish value to `YYYY-MM-DD`.
 *
 * Applied per column rather than inside stringifyValue, because guessing that
 * an arbitrary string is a date and truncating it would silently damage any
 * text field that happened to look like one. The API returns full timestamps
 * (`2026-08-14T00:00:00.000Z`) where only the day is meaningful, and dumping
 * that into a spreadsheet is both noisy and awkward to filter on.
 */
export function isoDate(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  // Use the UTC components: the API sends midnight UTC, and reading it in a
  // timezone behind UTC would roll the date back a day.
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

/** Characters that make a spreadsheet treat a cell as a formula. */
const FORMULA_PREFIX = /^[=+\-@\t\r]/;

/**
 * Neutralise a value that a spreadsheet would otherwise evaluate.
 *
 * The apostrophe is Excel's own "literal text" marker: it forces text parsing
 * and is not shown in the cell or included when the value is copied out.
 */
export function neutralizeFormula(text: string): string {
  return FORMULA_PREFIX.test(text) ? `'${text}` : text;
}

/* -------------------------------------------------------------------------- */
/* CSV                                                                         */
/* -------------------------------------------------------------------------- */

/** RFC 4180: quote when the value contains a delimiter, quote or newline. */
function csvCell(raw: string): string {
  const text = neutralizeFormula(raw);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCSV<T>(rows: T[], columns: ExportColumn<T>[]): string {
  const lines = [
    columns.map((c) => csvCell(c.header)).join(','),
    ...rows.map((row) =>
      columns.map((c) => csvCell(stringifyValue(c.value(row)))).join(',')
    ),
  ];

  // CRLF per RFC 4180. The BOM is what makes Excel on Windows read the file as
  // UTF-8 — without it, any non-ASCII name is mojibake on open.
  return '﻿' + lines.join('\r\n');
}

/* -------------------------------------------------------------------------- */
/* XLSX                                                                        */
/* -------------------------------------------------------------------------- */

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    // XML 1.0 forbids most control characters outright; a stray 0x00-0x1F from
    // a pasted value would make the whole workbook unreadable.
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
}

/** 0 -> A, 25 -> Z, 26 -> AA … */
function columnName(index: number): string {
  let name = '';
  let n = index;
  while (n >= 0) {
    name = String.fromCharCode((n % 26) + 65) + name;
    n = Math.floor(n / 26) - 1;
  }
  return name;
}

function sheetXml(matrix: string[][]): string {
  const rows = matrix
    .map((cells, r) => {
      const tags = cells
        .map((value, c) => {
          if (value === '') return '';
          // inlineStr keeps every value textual and, because an inline string is
          // never parsed as a formula, doubles as injection protection.
          return `<c r="${columnName(c)}${r + 1}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(
            value
          )}</t></is></c>`;
        })
        .join('');
      return `<row r="${r + 1}">${tags}</row>`;
    })
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${rows}</sheetData></worksheet>`;
}

/** Sheet names cannot exceed 31 chars or contain : \ / ? * [ ] */
function safeSheetName(name: string): string {
  const cleaned = name.replace(/[:\\/?*[\]]/g, ' ').trim();
  return (cleaned || 'Sheet1').slice(0, 31);
}

export function toXLSX<T>(
  rows: T[],
  columns: ExportColumn<T>[],
  sheetName = 'Sheet1'
): Blob {
  const matrix: string[][] = [
    columns.map((c) => c.header),
    ...rows.map((row) => columns.map((c) => stringifyValue(c.value(row)))),
  ];

  const sheet = safeSheetName(sheetName);

  const files: Record<string, string> = {
    '[Content_Types].xml': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>`,

    '_rels/.rels': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`,

    'xl/workbook.xml': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${escapeXml(
      sheet
    )}" sheetId="1" r:id="rId1"/></sheets></workbook>`,

    'xl/_rels/workbook.xml.rels': `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`,

    'xl/worksheets/sheet1.xml': sheetXml(matrix),
  };

  return zipSync(files, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

/* -------------------------------------------------------------------------- */
/* minimal ZIP writer (stored / no compression)                                */
/* -------------------------------------------------------------------------- */

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = crcTable[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * Build a ZIP archive with every entry stored rather than deflated.
 *
 * Trading size for having no compression dependency: an exported workbook is
 * XML text, so the archive is larger than a normal .xlsx, but it is a valid
 * one that Excel, LibreOffice and Numbers all open.
 */
function zipSync(files: Record<string, string>, mimeType: string): Blob {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const [path, content] of Object.entries(files)) {
    const nameBytes = encoder.encode(path);
    const dataBytes = encoder.encode(content);
    const crc = crc32(dataBytes);

    const local = new Uint8Array(30 + nameBytes.length + dataBytes.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true); // local file header signature
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0x0800, true); // flags: UTF-8 filenames
    lv.setUint16(8, 0, true); // method: stored
    lv.setUint16(10, 0, true); // mod time
    lv.setUint16(12, 0x0021, true); // mod date (1980-01-01; ZIP has no "unset")
    lv.setUint32(14, crc, true);
    lv.setUint32(18, dataBytes.length, true); // compressed size
    lv.setUint32(22, dataBytes.length, true); // uncompressed size
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, 0, true); // extra field length
    local.set(nameBytes, 30);
    local.set(dataBytes, 30 + nameBytes.length);
    locals.push(local);

    const central = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true); // central directory signature
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, 0, true);
    cv.setUint16(14, 0x0021, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, dataBytes.length, true);
    cv.setUint32(24, dataBytes.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, 0, true); // extra
    cv.setUint16(32, 0, true); // comment
    cv.setUint16(34, 0, true); // disk number
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true); // offset of local header
    central.set(nameBytes, 46);
    centrals.push(central);

    offset += local.length;
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true); // end of central directory
  ev.setUint16(8, centrals.length, true);
  ev.setUint16(10, centrals.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  // Flatten into one buffer rather than handing Blob an array of views: it
  // keeps the archive contiguous and sidesteps the ArrayBufferLike/ArrayBuffer
  // distinction that BlobPart draws.
  const parts = [...locals, ...centrals, end];
  const total = parts.reduce((n, p) => n + p.length, 0);
  const archive = new Uint8Array(total);
  let cursor = 0;
  for (const part of parts) {
    archive.set(part, cursor);
    cursor += part.length;
  }

  return new Blob([archive], { type: mimeType });
}

/* -------------------------------------------------------------------------- */
/* download                                                                    */
/* -------------------------------------------------------------------------- */

/** Strip characters that are illegal in filenames on Windows or POSIX. */
export function safeFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|\x00-\x1F]/g, '-').replace(/\s+/g, '-').slice(0, 120);
}

/** Generate YYYYMMDDHHMMSS timestamp string */
export function getExportTimestamp(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

/** `name_YYYYMMDDHHMMSS.ext` — sortable, standard timestamp format */
export function timestampedName(base: string, extension: string): string {
  const stamp = getExportTimestamp();
  const cleanExt = extension.replace(/^\./, '');
  const cleanBase = safeFilename(base).replace(/_$/, '');
  return `${cleanBase}_${stamp}.${cleanExt}`;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  // Firefox only dispatches the click if the anchor is in the document.
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking synchronously cancels the download in Safari, so yield first.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Serialise rows in the requested format and hand the file to the browser.
 *
 * Returns the row count so callers can report what was exported.
 */
export function exportRows<T>(
  rows: T[],
  columns: ExportColumn<T>[],
  format: ExportFormat,
  baseName: string
): number {
  if (format === 'csv') {
    const blob = new Blob([toCSV(rows, columns)], { type: 'text/csv;charset=utf-8' });
    downloadBlob(blob, timestampedName(baseName, 'csv'));
  } else if (format === 'xlsx') {
    downloadBlob(toXLSX(rows, columns, baseName), timestampedName(baseName, 'xlsx'));
  } else {
    const objects = rows.map((row) => {
      const out: Record<string, string> = {};
      for (const c of columns) out[c.header] = stringifyValue(c.value(row));
      return out;
    });
    const blob = new Blob([JSON.stringify(objects, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    downloadBlob(blob, timestampedName(baseName, 'json'));
  }
  return rows.length;
}
